<template>
    <div>
      <Header />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Manage Journals</h5>
          <p class="page-sub">View, approve and manage journal postings</p>
        </div>
        <div class="page-actions">
  
          <!-- Search -->
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input type="text" class="search-input" placeholder="Search journals..." @input="tableData.page = 1; fetchData()" v-model="tableData.search" />
          </div>
  
          <!-- Status Filter -->
          <select v-model="tableData.status" @change="tableData.page = 1; fetchData()" class="filter-select">
            <option :value="['posted']">Posted</option>
            <option :value="['draft']">Draft</option>
            <option :value="['draft','posted']">All Statuses</option>
          </select>
  
          <!-- Approval Filter -->
          <select v-model="tableData.approval_status" @change="tableData.page = 1; fetchData()" class="filter-select">
            <option :value="['pending']">Pending</option>
            <option :value="['approved']">Approved</option>
            <option :value="['rejected']">Rejected</option>
            <option :value="['pending','approved','rejected']">All Approvals</option>
          </select>
  
          <!-- Export -->
          <div class="dropdown">
            <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <font-awesome-icon icon="download" /> Export
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><a class="dropdown-item" href="#" @click="drawPdf(print_content, headerArrayColumn, 'p', 'ledger_group', 'Ledger Groups')">
                <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> PDF
              </a></li>
              <li><a class="dropdown-item" href="#" @click="reusableMixinToCSV('ledger_group.csv')">
                <font-awesome-icon icon="file-csv" class="me-2 text-success" /> CSV
              </a></li>
            </ul>
          </div>
  
          <!-- Bulk Action -->
          <div class="dropdown" v-if="selectedModel.length > 0">
            <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <font-awesome-icon icon="tasks" /> Bulk Action
              <span class="selection-badge">{{ selectedModel.length }}</span>
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li>
                <button class="dropdown-item" @click="getUpdateType('approve')"
                  data-bs-toggle="modal" data-bs-target="#bulkModal"
                  :disabled="permission.approve_expense != 1">
                  <font-awesome-icon icon="check" class="me-2 text-success" /> Approve
                </button>
              </li>
              <li>
                <button class="dropdown-item" @click="getUpdateType('reject')"
                  data-bs-toggle="modal" data-bs-target="#bulkModal"
                  :disabled="permission.approve_expense != 1">
                  <font-awesome-icon icon="times" class="me-2 text-warning" /> Reject
                </button>
              </li>
              <li><hr class="dropdown-divider" /></li>
              <li>
                <button class="dropdown-item text-danger" @click="getUpdateType('delete')"
                  data-bs-toggle="modal" data-bs-target="#bulkModal"
                  :disabled="permission.delete_expense != 1">
                  <font-awesome-icon icon="trash" class="me-2" /> Delete
                </button>
              </li>
            </ul>
          </div>
  
          <!-- Bulk Upload -->
          <button v-if="permission.create_journal == 1" class="btn-outline" @click="openJournalUploadModal">
            <font-awesome-icon icon="upload" /> Upload
          </button>

          <!-- New Posting -->
          <router-link :to="$url('/post-transaction')" class="btn-primary-action"
            :class="permission.post_transaction != 1 ? 'disabled' : ''">
            <font-awesome-icon icon="plus" /> New Posting
          </router-link>
  
        </div>
      </div>
  
      <!-- Data Card -->
      <div class="data-card">
  
        <!-- Loading -->
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted-cell">Loading journals...</p>
        </div>
  
        <!-- 403 -->
        <div v-else-if="permission_status == 403" class="state-block">
          <div class="state-icon state-icon--red">
            <font-awesome-icon icon="lock" />
          </div>
          <p class="state-title">Access Denied</p>
          <p class="state-sub">You don't have permission to view this resource.</p>
        </div>
  
        <!-- Empty -->
        <div v-else-if="details.length === 0" class="state-block">
          <div class="state-icon state-icon--blue">
            <font-awesome-icon icon="book" />
          </div>
          <p class="state-title">No journals found</p>
          <p class="state-sub">No journal postings match your current filters.</p>
        </div>
  
        <!-- Table -->
        <template v-else>
          <div class="table-toolbar">
            <span class="table-count">
              Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong>
            </span>
            <div class="d-flex align-items-center gap-2">
              <label class="table-count">Rows:</label>
              <select v-model.number="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
                <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
              </select>
            </div>
          </div>
  
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th style="width:40px">
                    <input type="checkbox" v-model="allSelected" @change="selectAll" class="form-check-input" />
                  </th>
                  <th>#</th>
                  <th>Reference</th>
                  <th class="text-right">Amount</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Approval</th>
                  <th class="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in details" :key="row.id">
  
                  <td>
                    <input type="checkbox" v-model="selectedModel" :value="row.id" class="form-check-input" />
                  </td>
  
                  <td class="text-muted-cell">{{ index + 1 }}</td>
  
                  <td>
                    <span class="batch-code">{{ row.reference_no }}</span>
                  </td>
  
                  <td class="text-right fw-bold amount-value">
                    {{ formatPrice(row.amount) }}
                  </td>
  
                  <td class="text-muted-cell">{{ formatDate(row.date) }}</td>
  
                  <td>
                    <span class="status-badge" :class="row.status === 'posted' ? 'status--posted' : 'status--draft'">
                      {{ row.status }}
                    </span>
                    <span v-if="row.status === 'draft' && row.approval_status === 'pending'" class="workflow-badge">
                      Awaiting Approval
                    </span>
                  </td>
  
                  <td>
                    <template v-if="row.source === 'sales' || row.source === 'purchase' || row.source === 'payroll_periods'">
                      <span class="approval-badge approval--na">System</span>
                    </template>
                    <template v-else>
                      <span class="approval-badge"
                        :class="{
                          'approval--pending':  row.approval_status === 'pending',
                          'approval--approved': row.approval_status === 'approved',
                          'approval--rejected': row.approval_status === 'rejected',
                        }">
                        {{ row.approval_status }}
                      </span>
                    </template>
                  </td>
  
                  <td class="col-actions">
                    <div class="action-btns">
                      <button class="action-btn action-btn--view"
                        :class="permission.view_expense != 1 ? 'disabled' : ''"
                        :disabled="permission.view_expense != 1"
                        @click="viewDetailsModal(index)"
                        title="View Details">
                        <font-awesome-icon icon="eye" />
                      </button>
                      <button v-if="row.status != 1 && row.source == 'manual'"
                        class="action-btn action-btn--edit"
                        :class="permission.update_expense != 1 ? 'disabled' : ''"
                        :disabled="permission.update_expense != 1"
                        @click="openUpdateModal(index)"
                        title="Update Posting">
                        <font-awesome-icon icon="pen" />
                      </button>
                      <button v-if="row.status != 1"
                        class="action-btn action-btn--delete"
                        :class="permission.delete_expense != 1 ? 'disabled' : ''"
                        :disabled="permission.delete_expense != 1"
                        @click="openDeleteModal(index)"
                        title="Delete">
                        <font-awesome-icon icon="trash" />
                      </button>
                      <template v-if="row.approval_status === 'pending' && row.source === 'manual'">
                        <button class="action-btn action-btn--approve"
                          :class="permission.approve_expense != 1 ? 'disabled' : ''"
                          :disabled="permission.approve_expense != 1"
                          @click="openApproveModal(index, 'approve')"
                          title="Approve">
                          <font-awesome-icon icon="check" />
                        </button>
                        <button class="action-btn action-btn--reject"
                          :class="permission.approve_expense != 1 ? 'disabled' : ''"
                          :disabled="permission.approve_expense != 1"
                          @click="openApproveModal(index, 'reject')"
                          title="Reject">
                          <font-awesome-icon icon="ban" />
                        </button>
                      </template>
                    </div>
                  </td>
  
                </tr>
              </tbody>
            </table>
          </div>
  
          <div v-if="pagination.lastPage > 1" class="pagination-bar">
            <span class="pagination-info">Showing {{ pagination.from }}–{{ pagination.to }} of {{ pagination.total }}</span>
            <div class="pagination-controls">
              <button class="pg-btn" :disabled="pagination.currentPage <= 1" @click="tableData.page = 1; fetchData()">«</button>
              <button class="pg-btn" :disabled="pagination.currentPage <= 1" @click="tableData.page = pagination.currentPage - 1; fetchData()">‹ Prev</button>
              <span class="pg-pages">
                <button v-for="p in pageRange" :key="p" class="pg-num" :class="{ 'pg-num--active': p === pagination.currentPage }" @click="tableData.page = p; fetchData()">{{ p }}</button>
              </span>
              <button class="pg-btn" :disabled="pagination.currentPage >= pagination.lastPage" @click="tableData.page = pagination.currentPage + 1; fetchData()">Next ›</button>
              <button class="pg-btn" :disabled="pagination.currentPage >= pagination.lastPage" @click="tableData.page = pagination.lastPage; fetchData()">»</button>
            </div>
          </div>
        </template>
      </div>
  
  
      <!-- ── View Detail Modal ── -->
      <div class="modal fade" id="view_detail" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-lg">
          <div class="modal-content modal-clean">
            <div class="modal-header-clean">
              <div class="modal-title-icon" style="background:rgba(0,74,173,.1);color:#004AAD;">
                <font-awesome-icon icon="file-alt" />
              </div>
              <div>
                <h6 class="modal-clean-title">Journal Detail</h6>
                <p class="modal-clean-sub">{{ currentData.reference_no }}</p>
              </div>
              <button type="button" class="btn-close ms-auto" @click="closeDetailModal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <div class="table-wrap">
                <table class="data-table">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Account</th>
                      <th class="text-right">Amount</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(entry, i) in currentData.entries" :key="i">
                      <td><span class="batch-code">{{ currentData.reference_no }}</span></td>
                      <td>{{ entry.account ? entry.account.name : '—' }}</td>
                      <td class="text-right fw-bold">{{ entry.debit > 0 ? formatPrice(entry.debit) : formatPrice(entry.credit) }}</td>
                      <td>
                        <span class="approval-badge" :class="entry.debit > 0 ? 'approval--rejected' : 'approval--approved'">
                          {{ entry.debit > 0 ? 'Debit' : 'Credit' }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="modal-footer border-0 pt-0">
              <button type="button" @click="closeDetailModal" class="btn-outline">Close</button>
            </div>
          </div>
        </div>
      </div>
  
  
      <!-- ── Update Modal ── -->
      <div class="modal fade" id="update_modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-lg">
          <div class="modal-content modal-clean">
            <div class="modal-header-clean">
              <div class="modal-title-icon" style="background:rgba(13,148,136,.1);color:#0d9488;">
                <font-awesome-icon icon="pen" />
              </div>
              <div>
                <h6 class="modal-clean-title">Update Journal Posting</h6>
                <p class="modal-clean-sub">{{ currentData.reference_no }}</p>
              </div>
              <button type="button" class="btn-close ms-auto" @click="closeUpdateModal" aria-label="Close"></button>
            </div>
            <form @submit.prevent="updateJournal">
              <div class="modal-body">
                <div class="modal-fields-grid">
                  <div class="field-group">
                    <label class="field-label">Transaction Date <span class="req">*</span></label>
                    <Datepicker v-model="journal.transaction_date" :enableTimePicker="true" class="clean-datepicker" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">Transaction Type <span class="req">*</span></label>
                    <select class="field-input field-select" v-model="journal.transaction_type">
                      <option value="" disabled>Choose type</option>
                      <option value="debit">Debit</option>
                      <option value="credit">Credit</option>
                    </select>
                  </div>
                  <div class="field-group">
                    <label class="field-label">Debit Account <span class="req">*</span></label>
                    <select v-model="journal.debit_account" class="field-input field-select">
                      <option disabled value="">Select account</option>
                      <optgroup v-for="(accs, type) in accounts" :key="type" :label="capitalize(type)">
                        <option v-for="acc in accs" :key="acc.id" :value="acc.id">{{ acc.name }}</option>
                      </optgroup>
                    </select>
                  </div>
                  <div class="field-group">
                    <label class="field-label">Credit Account <span class="req">*</span></label>
                    <select v-model="journal.credit_account" class="field-input field-select">
                      <option disabled value="">Select account</option>
                      <optgroup v-for="(accs, type) in accounts" :key="type" :label="capitalize(type)">
                        <option v-for="acc in accs" :key="acc.id" :value="acc.id">{{ acc.name }}</option>
                      </optgroup>
                    </select>
                  </div>
                  <div class="field-group">
                    <label class="field-label">Debit Amount <span class="req">*</span></label>
                    <input type="text" class="field-input" v-model="journal.debit_amount"
                      onkeypress="if(isNaN(this.value+String.fromCharCode(event.keyCode)))return false;" />
                  </div>
                  <div class="field-group">
                    <label class="field-label">Credit Amount <span class="req">*</span></label>
                    <input type="text" class="field-input" v-model="journal.credit_amount"
                      onkeypress="if(isNaN(this.value+String.fromCharCode(event.keyCode)))return false;" />
                  </div>
                  <div class="field-group field-group--full">
                    <label class="field-label">Description</label>
                    <textarea class="field-input" v-model="journal.description" rows="3" placeholder="Optional description"></textarea>
                  </div>
                </div>
              </div>
              <div class="modal-footer border-0 pt-0">
                <button type="button" @click="closeUpdateModal" class="btn-outline">Cancel</button>
                <button type="submit" class="btn-primary-action" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  {{ loading ? 'Saving...' : 'Update Journal' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
  
  
      <!-- ── Delete Modal ── -->
      <div class="modal fade" id="delete_modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-sm">
          <div class="modal-content modal-clean">
            <div class="modal-icon-wrap">
              <div class="modal-icon modal-icon--danger">
                <font-awesome-icon icon="trash" />
              </div>
            </div>
            <div class="modal-body-clean">
              <p class="modal-heading">Delete Journal?</p>
              <p class="modal-subtext">
                This will permanently delete posting <strong>{{ currentData.reference_no }}</strong>. This action cannot be undone.
              </p>
            </div>
            <div class="modal-actions">
              <button type="button" class="modal-btn modal-btn--cancel" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="modal-btn modal-btn--confirm modal-btn--danger" data-bs-dismiss="modal" @click.prevent="deleteJournal">
                <font-awesome-icon icon="trash" class="me-2" /> Delete
              </button>
            </div>
          </div>
        </div>
      </div>
  
  
      <!-- ── Approve / Reject Modal ── -->
      <div class="modal fade" id="approve_modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-sm">
          <div class="modal-content modal-clean">
            <div class="modal-icon-wrap">
              <div class="modal-icon" :class="approve_title === 'approve' ? 'modal-icon--success' : 'modal-icon--warning'">
                <font-awesome-icon :icon="approve_title === 'approve' ? 'check' : 'times'" />
              </div>
            </div>
            <div class="modal-body-clean">
              <p class="modal-heading">{{ approve_title === 'approve' ? 'Approve Journal?' : 'Reject Journal?' }}</p>
              <p class="modal-subtext">
                Are you sure you want to {{ approve_title }} posting <strong>{{ currentData.reference_no }}</strong>?
              </p>
            </div>
            <div class="modal-actions">
              <button type="button" class="modal-btn modal-btn--cancel" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="modal-btn modal-btn--confirm" data-bs-dismiss="modal"
                :class="approve_title === 'approve' ? 'modal-btn--success' : 'modal-btn--warning'"
                @click.prevent="approveJournal">
                <font-awesome-icon :icon="approve_title === 'approve' ? 'check' : 'times'" class="me-2" />
                {{ approve_title === 'approve' ? 'Yes, Approve' : 'Yes, Reject' }}
              </button>
            </div>
          </div>
        </div>
      </div>
  
  
      <!-- ── Journal Bulk Upload Modal ── -->
      <div class="modal fade" id="journal_upload_modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content modal-clean">
            <div class="modal-header-clean">
              <div class="modal-title-icon" style="background:rgba(0,74,173,.1);color:#004AAD;">
                <font-awesome-icon icon="upload" />
              </div>
              <div>
                <h6 class="modal-clean-title">Bulk Upload Journal Postings</h6>
                <p class="modal-clean-sub">Import multiple journal entries from a spreadsheet</p>
              </div>
              <button type="button" class="btn-close ms-auto" @click="closeJournalUploadModal" aria-label="Close"></button>
            </div>
            <div class="modal-body" style="display:flex;flex-direction:column;gap:16px;">
              <!-- Template download -->
              <div class="jup-template-row">
                <font-awesome-icon icon="file-excel" class="jup-template-icon" />
                <div>
                  <p class="jup-template-title">Download Template</p>
                  <p class="jup-template-sub">Each row creates one balanced journal (debit = credit).</p>
                </div>
                <button class="btn-template-dl" @click="downloadJournalTemplate">
                  <font-awesome-icon icon="download" /> Download
                </button>
              </div>

              <!-- Column chips -->
              <div class="upload-cols-hint">
                <span class="col-chip">date</span>
                <span class="col-chip">description</span>
                <span class="col-chip">transaction_type</span>
                <span class="col-chip">debit_account</span>
                <span class="col-chip">credit_account</span>
                <span class="col-chip">amount</span>
              </div>
              <p class="upload-types-note">
                Use exact account names from your chart of accounts.
                <code>transaction_type</code> should be <code>debit</code> or <code>credit</code>.
              </p>

              <!-- File picker -->
              <div class="field-group">
                <label class="field-label">Select File <span class="req">*</span></label>
                <input type="file" class="field-input upload-file-input" accept=".xlsx,.xls,.csv"
                  ref="journalFileInput" @change="onJournalFileChange" />
              </div>

              <!-- Result -->
              <div v-if="jUploadResult" class="upload-result-box"
                :class="jUploadResult.errors && jUploadResult.errors.length ? 'upload-result-box--warn' : 'upload-result-box--success'">
                <p class="upload-result-title">
                  <font-awesome-icon :icon="jUploadResult.errors && jUploadResult.errors.length ? 'exclamation-triangle' : 'check-circle'" class="me-2" />
                  {{ jUploadResult.message }}
                </p>
                <ul v-if="jUploadResult.errors && jUploadResult.errors.length" class="upload-error-list">
                  <li v-for="(err, i) in jUploadResult.errors" :key="i">{{ err }}</li>
                </ul>
              </div>
            </div>
            <div class="modal-footer border-0 pt-0">
              <button type="button" @click="closeJournalUploadModal" class="btn-outline">Close</button>
              <button class="btn-primary-action" @click="submitJournalUpload" :disabled="!jUploadFile || jUploadLoading">
                <span v-if="jUploadLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                {{ jUploadLoading ? 'Uploading...' : 'Upload Journals' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Bulk Modal ── -->
      <div class="modal fade" id="bulkModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-sm">
          <div class="modal-content modal-clean">
            <div class="modal-icon-wrap">
              <div class="modal-icon" :class="{
                'modal-icon--success': approve_title === 'approve',
                'modal-icon--warning': approve_title === 'reject',
                'modal-icon--danger':  approve_title === 'delete',
              }">
                <font-awesome-icon :icon="approve_title === 'delete' ? 'trash' : approve_title === 'approve' ? 'check' : 'times'" />
              </div>
            </div>
            <div class="modal-body-clean">
              <p class="modal-heading">Bulk {{ approve_title }}?</p>
              <p class="modal-subtext">
                This will {{ approve_title }} <strong>{{ selectedModel.length }}</strong> selected journal posting(s). Are you sure?
              </p>
            </div>
            <div class="modal-actions">
              <button type="button" class="modal-btn modal-btn--cancel" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="modal-btn modal-btn--confirm" data-bs-dismiss="modal"
                :class="{
                  'modal-btn--success': approve_title === 'approve',
                  'modal-btn--warning': approve_title === 'reject',
                  'modal-btn--danger':  approve_title === 'delete',
                }"
                @click.prevent="bulkUpdate">
                Confirm
              </button>
            </div>
          </div>
        </div>
      </div>
  
    </div>
  </template>
  
  <script>
  import { reusableMixin } from '../../../lib/reusableMixin';
  import Header from './component/header.vue';
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { useStore } from 'vuex';
  import moment from 'moment';
  
  export default {
    components: { Header },
    mixins: [reusableMixin],
  
    data() {
      return {
        print_content: [],
        headerArrayColumn: ['S/N', 'Reference', 'Amount', 'Date', 'Status', 'Approval'],
        details: [], statuses: [], loading: false,
        currentData: {}, allSelected: false, selectedModel: [],
        accounts: {}, approve_title: '', approve_status: '',
        permission_status: null,
        crudLoading: false,
  
        journal: {
          transaction_date: new Date(),
          debit_account: '', credit_account: '',
          debit_amount: 0.00, credit_amount: 0.00,
          description: '', transaction_type: '',
        },
        jUploadFile: null, jUploadLoading: false, jUploadResult: null,
  
        tableData: { page: 1,
          draw: 0, length: 5, search: '', column: 0, dir: 'desc', id: '',
          status: ['draft', 'posted'],
          approval_status: ['pending', 'approved', 'rejected'],
        },
        pagination: { lastPage:'', currentPage:'', total:'', lastPageUrl:'', nextPageUrl:'', prevPageUrl:'', from:'', to:'' },
        toast: useToast(),
        store: useStore(),
      };
    },
  
    computed: {
      permission() { return this.store.state.getCurrentUserPermission; },
      pageRange() {
        if (!this.pagination.lastPage) return [];
        const cur = this.pagination.currentPage, last = this.pagination.lastPage;
        const range = [];
        for (let i = Math.max(1, cur - 2); i <= Math.min(last, cur + 2); i++) range.push(i);
        return range;
      },
    },
  
    created() { this.fetchData(); this.fetchCategory(); },
  
    methods: {
      dataToPrint(details) {
        this.print_content = details.map((d, idx) => ({ sn: idx + 1, name: d.reference_no }));
      },
  
      fetchData(url = '/accounting/journal') {
        this.loading = true;
        this.tableData.draw++;
        axios.get(url, { params: this.tableData })
          .then(response => {
            const data    = response.data;
            this.details  = data.data.data;
            this.statuses = this.details.map(c => c.status);
            this.dataToPrint(this.details);
            this.configPagination(data.data);
            this.loading = false;
          })
          .catch(error => {
            this.loading = false;
            if (error?.response?.status == 403) {
              this.permission_status = 403;
              this.toast.error(error.response.data.message);
            }
          });
      },
  
      async fetchCategory() {
        axios.get('/accounting/active-ledgers').then(res => { this.accounts = res?.data?.data; }).catch(() => {});
      },
  
      async updateJournal() {
        this.loading = true;
        axios.put(`/accounting/journal/${this.currentData.id}`, this.journal)
          .then(() => { this.toast.success('Updated successfully'); this.fetchData(); this.loading = false; })
          .catch(error => {
            this.loading = false;
            const msg = error.response?.data?.message;
            this.toast.error(Array.isArray(msg) ? msg[0] : msg ?? 'Something went wrong');
          });
      },
  
      async approveJournal() {
        axios.put(`accounting/journal/approve/${this.currentData.id}`, { title: this.approve_status })
          .then(res => { this.toast.success(res.data.message); this.approve_title = ''; this.approve_status = ''; this.fetchData(); })
          .catch(error => { this.toast.error(error.response?.data?.message ?? 'Something went wrong'); });
      },
  
      async deleteJournal() {
        axios.delete(`/accounting/journal/${this.currentData.id}`)
          .then(() => { this.toast.success('Deleted successfully'); this.fetchData(); })
          .catch(error => { this.toast.error(error.response?.data?.message ?? 'Could not delete posting'); });
      },
  
      bulkUpdate() {
        if (!this.selectedModel.length) { this.toast.error('Nothing selected'); return; }
        axios.post('/accounting/journal/bulk-action', { ids: this.selectedModel, title: this.approve_title })
          .then(res => { this.toast.success(res.data.message); this.selectedModel = []; this.fetchData(); })
          .catch(error => { this.toast.error(error.response?.data?.message ?? 'Something went wrong'); });
      },
  
      openJournalUploadModal() {
        this.jUploadFile = null; this.jUploadResult = null;
        if (this.$refs.journalFileInput) this.$refs.journalFileInput.value = '';
        window.$('#journal_upload_modal').modal('show');
      },
      closeJournalUploadModal() { window.$('#journal_upload_modal').modal('hide'); },
      onJournalFileChange(e) { this.jUploadFile = e.target.files[0] ?? null; this.jUploadResult = null; },

      downloadJournalTemplate() {
        axios.get('/accounting/journal/template', { responseType: 'blob' })
          .then(res => {
            const url  = URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url; link.download = 'journal_upload_template.xlsx';
            link.click(); URL.revokeObjectURL(url);
          })
          .catch(() => this.toast.error('Could not download template'));
      },

      async submitJournalUpload() {
        if (!this.jUploadFile) { this.toast.warning('Please select a file first'); return; }
        this.jUploadLoading = true;
        this.jUploadResult  = null;
        const form = new FormData();
        form.append('file', this.jUploadFile);
        axios.post('/accounting/journal/import', form, { headers: { 'Content-Type': 'multipart/form-data' } })
          .then(res => {
            const data = res.data;
            this.jUploadResult = { message: data.message, errors: data.data?.errors ?? [] };
            if (data.data?.created > 0) this.fetchData();
          })
          .catch(err => {
            const msg    = err.response?.data?.message ?? 'Upload failed';
            const errors = err.response?.data?.data?.errors ?? [];
            this.jUploadResult = { message: msg, errors };
            this.toast.error(msg);
          })
          .finally(() => { this.jUploadLoading = false; });
      },

      selectAll() { this.selectedModel = this.allSelected ? this.details.map(u => u.id) : []; },
      getUpdateType(type) { this.approve_title = type; },
      capitalize(text) { return text.charAt(0).toUpperCase() + text.slice(1); },
      formatDate(date) { return moment(date).format('DD/MM/YYYY'); },
      formatPrice(value) { if (!value) return '0'; return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
  
      openUpdateModal(index) {
        this.currentData = this.details[index];
        const e = this.currentData.entries;
        this.journal.transaction_date = this.currentData.date;
        this.journal.debit_account    = e[0].debit > 0 ? e[0].account_id : e[1].account_id;
        this.journal.credit_account   = e[0].credit > 0 ? e[0].account_id : e[1].account_id;
        this.journal.debit_amount     = e[0].debit > 0 ? e[0].debit : e[1].debit;
        this.journal.credit_amount    = e[0].credit > 0 ? e[0].credit : e[1].credit;
        this.journal.description      = this.currentData.description;
        this.journal.transaction_type = this.currentData.transaction?.type ?? '';
        window.$('#update_modal').modal('show');
      },
      openDeleteModal(index)  { this.currentData = this.details[index]; window.$('#delete_modal').modal('show'); },
      openApproveModal(index, name) {
        this.currentData     = this.details[index];
        this.approve_title   = name;
        this.approve_status  = name === 'approve' ? 'approved' : 'rejected';
        window.$('#approve_modal').modal('show');
      },
      viewDetailsModal(index) { this.currentData = this.details[index]; window.$('#view_detail').modal('show'); },
      closeDetailModal() { window.$('#view_detail').modal('hide'); },
      closeUpdateModal() { window.$('#update_modal').modal('hide'); },
  
      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  
  /* ── Filters / Search ── */
  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 32px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:190px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  .search-input::placeholder { color:#aab8cc; }
  
  .filter-select { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; cursor:pointer; }
  
  /* ── Buttons ── */
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; text-decoration:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-primary-action:hover { background:#003b8e; color:#fff; text-decoration:none; }
  .btn-primary-action.disabled { opacity:.5; pointer-events:none; }
  .btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; text-decoration:none; }
  .btn-outline:hover { background:#f4f7fb; color:#1a2b45; }
  .btn-outline::after { display:none !important; }
  
  /* ── Selection Badge ── */
  .selection-badge { background:#004AAD; color:#fff; border-radius:20px; padding:1px 7px; font-size:11px; font-weight:700; margin-left:4px; }
  
  /* ── Data Card ── */
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  /* ── States ── */
  .state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon  { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--blue { background:rgba(0,74,173,.1); color:#004AAD; }
  .state-icon--red  { background:rgba(224,53,53,.1); color:#e03535; }
  .state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub   { font-size:13px; color:#7a90b0; margin:0; }
  .mt-3        { margin-top:12px; }
  
  /* ── Toolbar ── */
  .table-toolbar { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .table-count   { font-size:13px; color:#7a90b0; }
  .rows-select   { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }
  .d-flex { display:flex; } .align-items-center { align-items:center; } .gap-2 { gap:8px; }
  
  /* ── Table ── */
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 14px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody td { padding:11px 14px; font-size:13.5px; vertical-align:middle; }
  .col-actions { width:160px; }
  .text-right      { text-align:right; }
  .text-muted-cell { color:#4a5e78; }
  .fw-bold         { font-weight:700; }
  .amount-value    { color:#004AAD; }
  
  /* ── Batch Code ── */
  .batch-code { font-family:monospace; font-size:12.5px; font-weight:600; color:#1a2b45; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:6px; padding:2px 8px; }
  
  /* ── Status Badges ── */
  .status-badge   { display:inline-block; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; border:1px solid transparent; text-transform:capitalize; }
  .status--posted { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  .status--draft  { background:#f8fafc; color:#7a90b0; border-color:#e4eaf2; }
  
  .workflow-badge { display:inline-block; border-radius:6px; padding:2px 8px; font-size:11px; font-weight:600; background:#eff6ff; color:#2563eb; border:1px solid #bfdbfe; margin-left:5px; white-space:nowrap; }
.approval-badge    { display:inline-block; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; border:1px solid transparent; text-transform:capitalize; }
  .approval--pending  { background:#fff7ed; color:#e08c00; border-color:#fed7aa; }
  .approval--approved { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
  .approval--rejected { background:#fff0f0; color:#e03535; border-color:#fac5c5; }
  .approval--na       { background:#f4f7fb; color:#7a90b0; border-color:#e4eaf2; }
  
  /* ── Action Buttons ── */
  .action-btns  { display:flex; align-items:center; gap:5px; flex-wrap:wrap; }
  .action-btn   { width:28px; height:28px; border-radius:7px; border:1px solid #e4eaf2; display:flex; align-items:center; justify-content:center; font-size:11px; cursor:pointer; transition:background .15s; background:#fff; }
  .action-btn:disabled,.action-btn.disabled { opacity:.4; cursor:not-allowed; }
  .action-btn--view    { color:#0d9488; } .action-btn--view:hover:not(:disabled)    { background:#f0fdfb; border-color:#a7f3d0; }
  .action-btn--edit    { color:#004AAD; } .action-btn--edit:hover:not(:disabled)    { background:#edf4ff; border-color:#c5d6f5; }
  .action-btn--delete  { color:#e03535; } .action-btn--delete:hover:not(:disabled)  { background:#fff0f0; border-color:#fac5c5; }
  .action-btn--approve { color:#16a34a; } .action-btn--approve:hover:not(:disabled) { background:#f0fdf4; border-color:#bbf7d0; }
  .action-btn--reject  { color:#e08c00; } .action-btn--reject:hover:not(:disabled)  { background:#fff7ed; border-color:#fed7aa; }
  
  /* ── Footer ── */
  .table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }
  
  /* ── Dropdown ── */
  .dropdown-menu    { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:170px; }
  .dropdown-item    { border-radius:6px; font-size:13px; padding:8px 12px; color:#1a2b45; background:transparent; border:none; width:100%; text-align:left; cursor:pointer; }
  .dropdown-item:hover { background:#f4f7fb; }
  .dropdown-item:disabled { opacity:.4; cursor:not-allowed; }
  .dropdown-divider { border-color:#f0f4f8; margin:4px 0; }
  
  /* ── Modal Clean ── */
  .modal-clean { border:none; border-radius:16px; box-shadow:0 20px 60px rgba(0,0,0,.14); overflow:hidden; }
  .modal-header-clean { display:flex; align-items:flex-start; gap:14px; padding:20px 24px 16px; border-bottom:1px solid #f0f4f8; }
  .modal-title-icon { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
  .modal-clean-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .modal-clean-sub   { font-size:12px; color:#7a90b0; margin:0; }
  
  /* ── Confirm Modals ── */
  .modal-icon-wrap { display:flex; justify-content:center; padding:28px 24px 0; }
  .modal-icon { width:60px; height:60px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:26px; }
  .modal-icon--danger  { background:rgba(224,53,53,.1);  color:#e03535; }
  .modal-icon--success { background:rgba(22,163,74,.1);  color:#16a34a; }
  .modal-icon--warning { background:rgba(224,140,0,.1);  color:#e08c00; }
  .modal-body-clean { padding:16px 28px 8px; text-align:center; }
  .modal-heading    { font-size:17px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
  .modal-subtext    { font-size:13.5px; color:#7a90b0; margin:0; line-height:1.6; }
  .modal-actions    { display:flex; gap:10px; padding:18px 24px 24px; justify-content:center; }
  .modal-btn        { display:inline-flex; align-items:center; justify-content:center; font-size:13px; font-weight:600; padding:9px 22px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; flex:1; }
  .modal-btn--cancel  { background:#f4f7fb; color:#1a2b45; border:1px solid #dde5f0; } .modal-btn--cancel:hover { background:#e8eef6; }
  .modal-btn--confirm { background:#004AAD; color:#fff; } .modal-btn--confirm:hover { background:#003b8e; }
  .modal-btn--danger  { background:#e03535 !important; }  .modal-btn--danger:hover  { background:#c42b2b !important; }
  .modal-btn--success { background:#16a34a !important; }  .modal-btn--success:hover { background:#14863d !important; }
  .modal-btn--warning { background:#e08c00 !important; }  .modal-btn--warning:hover { background:#b87100 !important; }
  
  /* ── Modal Form Fields ── */
  .modal-fields-grid  { display:grid; grid-template-columns:repeat(2,1fr); gap:16px; }
  .field-group        { display:flex; flex-direction:column; gap:6px; }
  .field-group--full  { grid-column:1/-1; }
  .field-label        { font-size:13px; font-weight:600; color:#1a2b45; }
  .req                { color:#e03535; }
  .field-input        { padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; transition:border-color .15s; font-family:inherit; resize:vertical; }
  .field-input:focus  { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  .field-select       { appearance:auto; cursor:pointer; }
  :deep(.clean-datepicker) { border:1px solid #dde5f0 !important; border-radius:8px !important; background:#fff !important; }
  :deep(.clean-datepicker input) { font-size:13.5px; color:#1a2b45; }
  
  /* ── Upload modal (journal) ── */
  .jup-template-row   { display:flex; align-items:center; gap:12px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:9px; padding:12px 14px; }
  .jup-template-icon  { font-size:22px; color:#16a34a; flex-shrink:0; }
  .jup-template-title { font-size:13px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .jup-template-sub   { font-size:11.5px; color:#7a90b0; margin:0; }
  .btn-template-dl    { display:inline-flex; align-items:center; gap:5px; margin-left:auto; background:#004AAD; color:#fff; font-size:12px; font-weight:600; padding:6px 13px; border-radius:7px; border:none; cursor:pointer; white-space:nowrap; flex-shrink:0; }
  .btn-template-dl:hover { background:#003b8e; }
  .upload-cols-hint   { display:flex; flex-wrap:wrap; gap:6px; }
  .col-chip           { background:#eff6ff; color:#004AAD; border:1px solid #bfdbfe; border-radius:5px; padding:2px 9px; font-size:11.5px; font-weight:600; font-family:monospace; }
  .upload-types-note  { font-size:11.5px; color:#7a90b0; margin:0; }
  .upload-types-note code { background:#f4f7fb; border-radius:4px; padding:1px 5px; font-size:11px; color:#1a2b45; }
  .upload-file-input  { padding:7px 10px; cursor:pointer; }
  .upload-result-box          { border-radius:8px; padding:12px 14px; }
  .upload-result-box--success { background:#f0fdf4; border:1px solid #bbf7d0; }
  .upload-result-box--warn    { background:#fffbeb; border:1px solid #fde68a; }
  .upload-result-title        { font-size:13px; font-weight:600; color:#1a2b45; margin:0 0 6px; display:flex; align-items:center; }
  .upload-error-list          { margin:0; padding-left:18px; }
  .upload-error-list li       { font-size:12px; color:#92400e; margin-bottom:3px; }

  /* ── Responsive ── */
  @media(max-width:991px) { .page-header,.page-actions { flex-direction:column; align-items:stretch; } .modal-fields-grid { grid-template-columns:1fr; } }
  </style>